import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, ArrowLeft, UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_ps-medical-logistics/artifacts/1dt4h2l7_Gemini_Generated_Image_dpwsejdpwsejdpws.png";

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [users, setUsers] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);

  async function loadAll() {
    try {
      const [r, c, u] = await Promise.all([
        api.get("/admin/provider-access-requests"),
        api.get("/admin/clinic-registrations"),
        api.get("/admin/users"),
      ]);
      setRequests(r.data);
      setClinics(c.data);
      setUsers(u.data);
    } catch (_e) {
      toast.error("Failed to load admin data");
    }
  }

  const loadAllStable = useCallback(loadAll, []);

  useEffect(() => {
    loadAllStable();
  }, [loadAllStable]);

  async function handleLogout() {
    await logout();
    navigate("/portal/login", { replace: true });
  }

  function openCreateFromRequest(req) {
    setPrefill({
      email: req.email,
      name: req.full_name,
      organization: req.organization,
      role: req.role === "clinic_admin" ? "clinic_admin" : "physician",
    });
    setCreateOpen(true);
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-slate-50/60 flex flex-col">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/portal" className="flex items-center gap-3" data-testid="admin-back-portal">
            <img src={LOGO_URL} alt="PS GLOBAL SERIES, LLC" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <div className="font-display text-[14px] font-bold tracking-tight text-slate-900">
                PS GLOBAL SERIES — Admin
              </div>
              <div className="eyebrow text-[9px] mt-0.5">Operations Console</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/portal"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 px-3 py-2 text-xs font-semibold rounded-sm"
              data-testid="admin-link-dashboard"
            >
              <ArrowLeft size={14} /> Dashboard
            </Link>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 text-xs font-semibold rounded-sm"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="flex-1 mx-auto max-w-7xl w-full px-6 lg:px-10 py-12">
        <div className="eyebrow">Operations</div>
        <h1 className="font-display mt-3 text-3xl sm:text-4xl font-light tracking-tight text-slate-900">
          Admin <span className="font-semibold">Console</span>
        </h1>

        <div className="mt-8 border-b border-slate-200 flex gap-6 text-sm">
          <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testId="tab-requests">
            Access Requests ({requests.length})
          </TabBtn>
          <TabBtn active={tab === "clinics"} onClick={() => setTab("clinics")} testId="tab-clinics">
            Clinic Registrations ({clinics.length})
          </TabBtn>
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} testId="tab-users">
            Users ({users.length})
          </TabBtn>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={() => {
              setPrefill(null);
              setCreateOpen(true);
            }}
            data-testid="admin-create-user-btn"
            className="inline-flex items-center gap-2 bg-[#B45309] hover:bg-[#92400E] text-white px-4 py-2.5 text-sm font-semibold rounded-sm"
          >
            <UserPlus size={15} /> Create Portal User
          </button>
        </div>

        <div className="mt-6 bg-white border border-slate-200">
          {tab === "requests" && (
            <Table
              testId="table-requests"
              empty="No provider access requests yet."
              rows={requests}
              columns={[
                { key: "full_name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "role", label: "Role" },
                { key: "organization", label: "Organization" },
                { key: "state", label: "State" },
                { key: "license_number", label: "License" },
                { key: "submitted_at", label: "Submitted", isDate: true },
              ]}
              rowAction={(r) => (
                <button
                  type="button"
                  onClick={() => openCreateFromRequest(r)}
                  className="text-xs font-semibold text-[#B45309] hover:text-[#92400E]"
                  data-testid={`request-issue-${r.email}`}
                >
                  Issue credentials
                </button>
              )}
            />
          )}
          {tab === "clinics" && (
            <Table
              testId="table-clinics"
              empty="No clinic registrations yet."
              rows={clinics}
              columns={[
                { key: "clinic_name", label: "Clinic" },
                { key: "contact_name", label: "Contact" },
                { key: "email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "address_city", label: "City" },
                { key: "address_state", label: "State" },
                { key: "submitted_at", label: "Submitted", isDate: true },
              ]}
            />
          )}
          {tab === "users" && (
            <Table
              testId="table-users"
              empty="No users yet."
              rows={users}
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "role", label: "Role" },
                { key: "organization", label: "Organization" },
                { key: "created_at", label: "Created", isDate: true },
              ]}
            />
          )}
        </div>
      </main>

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        prefill={prefill}
        onCreated={() => loadAll()}
      />
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`pb-3 -mb-px border-b-2 font-semibold tracking-tight transition-colors ${
        active
          ? "border-slate-900 text-slate-900"
          : "border-transparent text-slate-500 hover:text-slate-700"
      }`}
    >
      {children}
    </button>
  );
}

function Table({ rows, columns, empty, rowAction, testId }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-16 text-center text-slate-500 text-sm" data-testid={`${testId}-empty`}>
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid={testId}>
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.15em] font-bold text-slate-600"
              >
                {c.label}
              </th>
            ))}
            {rowAction && <th className="px-4 py-3" />}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, idx) => {
            const rowKey = r.id || r._id || r.email || `${r.submitted_at || ""}-${idx}`;
            return (
            <tr key={rowKey} className="border-b border-slate-100 hover:bg-slate-50/60">
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-slate-800">
                  {c.isDate
                    ? r[c.key]
                      ? new Date(r[c.key]).toLocaleString()
                      : "—"
                    : r[c.key] || "—"}
                </td>
              ))}
              {rowAction && <td className="px-4 py-3 text-right">{rowAction(r)}</td>}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function CreateUserDialog({ open, onOpenChange, prefill, onCreated }) {
  const initial = {
    name: "",
    email: "",
    organization: "",
    role: "physician",
    password: "",
  };
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        ...initial,
        ...(prefill || {}),
        password: "",
      });
    }
  }, [open, prefill]);

  function update(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/admin/users", form);
      toast.success("Portal user created", {
        description: `${form.email} can now sign in to the portal.`,
      });
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error("Could not create user", { description: msg });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="admin-create-user-dialog"
        className="sm:max-w-lg bg-white border border-slate-200 rounded-sm"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-semibold tracking-tight">
            Create Portal User
          </DialogTitle>
          <DialogDescription className="text-slate-600">
            Issue credentials to a verified physician or clinic administrator.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <Field label="Full name" required>
            <Input
              data-testid="cu-name"
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              required
              minLength={2}
            />
          </Field>
          <Field label="Email" required>
            <Input
              type="email"
              data-testid="cu-email"
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              required
            />
          </Field>
          <Field label="Organization">
            <Input
              data-testid="cu-organization"
              value={form.organization}
              onChange={(e) => update("organization", e.target.value)}
            />
          </Field>
          <Field label="Role" required>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger data-testid="cu-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="physician">Physician</SelectItem>
                <SelectItem value="clinic_admin">Clinic Admin</SelectItem>
                <SelectItem value="admin">Platform Admin</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Temporary password (min 8 chars)" required>
            <Input
              type="text"
              data-testid="cu-password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              required
              minLength={8}
            />
          </Field>

          <DialogFooter className="pt-2">
            <button
              type="submit"
              disabled={submitting}
              data-testid="cu-submit"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-semibold rounded-sm disabled:opacity-60"
            >
              {submitting && <Loader2 size={15} className="animate-spin" />}
              Create User
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[12px] uppercase tracking-[0.15em] font-bold text-slate-600">
        {label} {required && <span className="text-[#B45309]">*</span>}
      </Label>
      {children}
    </div>
  );
}
